import { useEffect } from 'react';
import { useIsConsumerSession } from '@/hooks/use-app-mode';
import { CONSUMER_STEPS, type ConsumerStepId } from '../lib/steps';
import { useConsumerPlan } from './use-consumer-plan';
import { markConsumerFlowVisited } from './use-consumer-resume';

/**
 * Saves the consumer's place in the flow, and notes this session has been here.
 * An advisor looking at the flow doesn't move the consumer's place in it, and a screen that is
 * redirecting elsewhere (`enabled: false`) never becomes the place to resume, so a blocked step can't
 * send the consumer round in a loop.
 */
export function useRememberStep(
  clientId: string,
  step: ConsumerStepId,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const isConsumer = useIsConsumerSession();
  const { plan, updatePlan } = useConsumerPlan(clientId);
  const shouldRemember =
    enabled && isConsumer && !!plan && CONSUMER_STEPS[step].resumable && plan.lastStep !== step;

  useEffect(() => {
    if (shouldRemember) updatePlan({ lastStep: step });
  }, [shouldRemember, step, updatePlan]);

  useEffect(() => markConsumerFlowVisited(clientId), [clientId]);
}
